import axios from "axios";
import { ethers } from "ethers";
import { connectWisdomWallet, ensureWisdomChain, silentWisdomAccount, wisdomRpcUrl } from "./chain";

const SIG_KEY = "wisdomWalletSig";

function emptySig() {
    return { wallet: null, signature: null, nonce: null, expiresAt: 0 };
}

function readStoredSig() {
    if (typeof sessionStorage === "undefined") {
        return emptySig();
    }
    try {
        const raw = sessionStorage.getItem(SIG_KEY);
        if (!raw) {
            return emptySig();
        }
        const parsed = JSON.parse(raw);
        if (!parsed || !parsed.wallet || !parsed.signature || !parsed.expiresAt) {
            return emptySig();
        }
        return {
            wallet: String(parsed.wallet).toLowerCase(),
            signature: parsed.signature,
            nonce: parsed.nonce,
            expiresAt: Number(parsed.expiresAt) || 0,
        };
    } catch (e) {
        return emptySig();
    }
}

function writeStoredSig(sig) {
    cachedSig = sig;
    if (typeof sessionStorage === "undefined") {
        return;
    }
    if (!sig || !sig.signature) {
        sessionStorage.removeItem(SIG_KEY);
        return;
    }
    sessionStorage.setItem(SIG_KEY, JSON.stringify(sig));
}

let cachedSig = readStoredSig();
let inflightSign = null;

export function cachedWalletSignature(wallet) {
    const w = (wallet || (typeof localStorage !== "undefined" ? localStorage.getItem("user") : "") || "").toLowerCase();
    if (!w) {
        return null;
    }
    if (!cachedSig.signature) {
        cachedSig = readStoredSig();
    }
    const now = Math.floor(Date.now() / 1000);
    if (cachedSig.wallet === w && cachedSig.signature && cachedSig.expiresAt - 30 > now) {
        return cachedSig;
    }
    return null;
}

export async function ensureWalletSignature(wallet, options) {
    const interactive = !options || options.interactive !== false;
    const w = (wallet || (typeof localStorage !== "undefined" ? localStorage.getItem("user") : "") || "").toLowerCase();
    if (!w || typeof window === "undefined") {
        return null;
    }
    const existing = cachedWalletSignature(w);
    if (existing) {
        return existing;
    }
    if (!interactive) {
        return null;
    }
    if (!window.ethereum) {
        return null;
    }
    if (inflightSign) {
        return inflightSign;
    }
    inflightSign = (async () => {
        const res = await fetch(`/api/auth/nonce?wallet=${encodeURIComponent(w)}`);
        if (!res.ok) {
            throw new Error("Could not get login nonce");
        }
        const data = await res.json();
        const checksum = ethers.getAddress(w);
        const messageHex = ethers.hexlify(ethers.toUtf8Bytes(data.message));
        const signature = await window.ethereum.request({
            method: "personal_sign",
            params: [messageHex, checksum],
        });
        const next = {
            wallet: w,
            signature,
            nonce: data.nonce,
            expiresAt: data.expiresAt,
        };
        writeStoredSig(next);
        return next;
    })();
    try {
        return await inflightSign;
    } finally {
        inflightSign = null;
    }
}

export function authHeaders(extra) {
    const headers = extra ? { ...extra } : {};
    const wallet = typeof localStorage !== "undefined" ? localStorage.getItem("user") : null;
    if (wallet) {
        headers["X-Wallet-Address"] = wallet;
    }
    const token = typeof localStorage !== "undefined" ? localStorage.getItem("wisdomAdminToken") : null;
    if (token) {
        headers["X-Admin-Token"] = token;
    }
    if (cachedSig.signature && cachedSig.wallet && wallet && cachedSig.wallet === wallet.toLowerCase()) {
        headers["X-Wallet-Signature"] = cachedSig.signature;
        headers["X-Wallet-Nonce"] = cachedSig.nonce;
    }
    return headers;
}

export async function loginWisdomWallet(address) {
    const sig = await ensureWalletSignature(address);
    if (!sig) {
        throw new Error("Wallet signature required");
    }
    const login = await fetch("/api/auth/login", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "X-Wallet-Address": sig.wallet || address,
            "X-Wallet-Signature": sig.signature,
            "X-Wallet-Nonce": sig.nonce,
        },
        body: "{}",
    });
    if (!login.ok) {
        throw new Error(await loginFailureMessage(login));
    }
    return address;
}

async function loginFailureMessage(res) {
    let error = "";
    try {
        const body = await res.json();
        error = (body && (body.error || body.message)) || "";
    } catch (e) {
        error = "";
    }
    if (error === "nonce_expired") {
        return "Login nonce expired. Sign in again.";
    }
    if (error === "nonce_mismatch") {
        return "Login nonce did not match. Sign in again.";
    }
    if (error === "signature_invalid") {
        return "Wallet signature did not match this login message.";
    }
    if (error === "signature_required") {
        return "Wallet signature required.";
    }
    return error || `Wallet login failed (${res.status}).`;
}

export async function sessionWisdomWallet(address, options) {
    const interactive = options && options.interactive === true;
    let sig;
    try {
        sig = await ensureWalletSignature(address, { interactive });
    } catch (e) {
        return false;
    }
    if (!sig) {
        return false;
    }
    const res = await fetch("/api/auth/session", {
        headers: {
            "X-Wallet-Address": sig.wallet || address,
            "X-Wallet-Signature": sig.signature,
            "X-Wallet-Nonce": sig.nonce,
        },
    });
    return res.ok;
}

async function readJson(res) {
    const text = await res.text();
    let data = {};
    try {
        data = text ? JSON.parse(text) : {};
    } catch (e) {
        data = { error: text };
    }
    if (!res.ok) {
        throw new Error(data.error || data.message || `Request failed (${res.status})`);
    }
    return data;
}

export async function courseQuote(courseId, options) {
    const interactive = options && options.interactive === true;
    const extra = { Accept: "application/json" };
    const headers = interactive ? await signedAuthHeaders(extra) : authHeaders(extra);
    const res = await fetch(`/api/bookings/mine/${encodeURIComponent(courseId)}`, { headers });
    if (res.status === 401) {
        return null;
    }
    return readJson(res);
}

function rpcAddress(value) {
    if (value == null || value === "") {
        throw new Error("Missing wallet or treasury address for payment.");
    }
    return ethers.getAddress(String(value));
}

function rpcQuantity(value) {
    if (value == null || value === "") {
        throw new Error("Missing payment amount from quote.");
    }
    return ethers.toQuantity(value);
}

async function waitForPayment(txHash) {
    const provider = new ethers.JsonRpcProvider(wisdomRpcUrl());
    for (let i = 0; i < 20; i++) {
        const receipt = await provider.getTransactionReceipt(txHash);
        if (receipt && Number(receipt.status) === 1) {
            return receipt;
        }
        if (receipt && Number(receipt.status) === 0) {
            throw new Error("WSD payment transaction failed");
        }
        await new Promise((resolve) => setTimeout(resolve, 1000));
    }
    throw new Error("WSD payment was sent but is not confirmed yet. Wait a few seconds and try again.");
}

export async function buyCourse(courseId) {
    const headers = await signedAuthHeaders({
        "Content-Type": "application/json",
        "Accept": "application/json",
    });
    const quote = await readJson(await fetch(`/api/bookings/mine/${encodeURIComponent(courseId)}`, { headers }));
    if (quote.paid) {
        return quote;
    }
    let txHash = null;
    if (Number(quote.price) > 0) {
        if (!window.ethereum) {
            throw new Error("MetaMask is required to pay with WSD");
        }
        await ensureWisdomChain();
        const from = (await silentWisdomAccount()) || (await connectWisdomWallet());
        txHash = await window.ethereum.request({
            method: "eth_sendTransaction",
            params: [{
                from: rpcAddress(from),
                to: rpcAddress(quote.treasury),
                value: rpcQuantity(quote.priceWei),
                chainId: "0x903",
            }],
        });
        await waitForPayment(txHash);
    }
    return readJson(await fetch(`/api/bookings/buy/${encodeURIComponent(courseId)}`, {
        method: "POST",
        headers,
        body: JSON.stringify({ txHash }),
    }));
}

export async function signedAuthHeaders(extra) {
    const wallet = typeof localStorage !== "undefined" ? localStorage.getItem("user") : null;
    if (wallet && !(typeof localStorage !== "undefined" && localStorage.getItem("wisdomAdminToken"))) {
        try {
            await ensureWalletSignature(wallet);
        } catch (e) {
            console.warn("wallet signature failed", e);
        }
    }
    return authHeaders(extra);
}

axios.interceptors.request.use(async (config) => {
    const method = (config.method || "get").toLowerCase();
    if (["post", "put", "patch", "delete"].includes(method)) {
        const wallet = typeof localStorage !== "undefined" ? localStorage.getItem("user") : null;
        if (wallet && !localStorage.getItem("wisdomAdminToken")) {
            try {
                await ensureWalletSignature(wallet);
            } catch (e) {
                console.warn("wallet signature failed", e);
            }
        }
    }
    config.headers = authHeaders(config.headers || {});
    return config;
});
